// The transport state machine (P2.7) — FR-02, written down before it was written.
//
// P2.7's rule, verbatim: "Cue is a state machine, and the reason to say so is that 'approximately
// right' cue is worse than none: a DJ's hands know this behaviour. Write the machine down before
// writing it, test every transition, and name the states after what the deck is doing."
//
// So here it is. Three phases, four events, twelve transitions, all of them enumerated:
//
// ```
//   phase        event           → phase        cue point        playhead
//   ─────────────────────────────────────────────────────────────────────────────────
//   paused       play-pressed    → playing      unchanged        stays
//   paused       cue-pressed     → previewing   SET to playhead  stays
//   paused       cue-released    → paused       unchanged        stays        (no-op)
//   paused       ended           → paused       unchanged        stays        (no-op)
//
//   playing      play-pressed    → paused       unchanged        stays
//   playing      cue-pressed     → previewing   unchanged        JUMP to cue
//   playing      cue-released    → playing      unchanged        stays        (no-op)
//   playing      ended           → paused       unchanged        stays
//
//   previewing   play-pressed    → playing      unchanged        stays
//   previewing   cue-pressed     → previewing   unchanged        JUMP to cue  (re-trigger)
//   previewing   cue-released    → paused       unchanged        JUMP to cue
//   previewing   ended           → paused       unchanged        JUMP to cue
// ```
//
// **How that table is the four behaviours FR-02 names**, because the mapping is not obvious from
// the table alone:
//
//   * *"set at pause point"* — `paused` + cue-pressed sets the cue where the playhead is. Pressing
//     cue is how a cue point gets made; there is no separate "set cue" button on a CDJ and there
//     is not one here.
//   * *"hold-to-preview"* — cue-pressed enters `previewing`, which runs the transport. Hold and it
//     keeps playing from the cue.
//   * *"release-returns"* — cue-released from `previewing` jumps the playhead back to the cue and
//     pauses. This is the one that makes cue feel like cue: you always end up exactly where you
//     started, no matter how long you held it.
//   * *"jump to cue"* — `playing` + cue-pressed. A **tap** is a very short preview, so it reads as
//     "return to cue and stop"; holding the same button previews from there instead. One button,
//     both behaviours, which is exactly how the hardware works — and the reason cue-stutter
//     (tapping repeatedly for a rhythmic effect) falls out for free rather than needing a feature.
//
// **The stale-event cases are in the table on purpose, not by omission.** `cue-released` while
// `paused` or `playing` is a no-op, and it happens for real: press cue while playing (→ previewing),
// press play to latch in (→ playing), then lift the finger. That last release must not stop the
// deck. A machine that only handled the tidy sequences would fail on that exact gesture, which is a
// common one.
//
// Pure, and living in `/platform/types` for two reasons. The worklet may import only `/audio/dsp`,
// `/platform/types` and `/platform/sab` (ADR-L1), and Architecture §7 puts transport transitions on
// the audio thread — so the machine has to be reachable from there. And a `(state, event) => state`
// function has no runtime state of its own, which is what this folder's "types, constants, and pure
// functions only" rule actually asks for. It is testable without a worklet, a browser, or audio.

/** Named for what the deck is doing, per P2.7's rule — not for how it got there. */
export type TransportPhase =
  /** Stopped. The playhead sits wherever it was left. */
  | 'paused'
  /** Running because the user asked it to run. */
  | 'playing'
  /** Running only while cue is held. Releasing returns to the cue point. */
  | 'previewing';

export type TransportEvent =
  | 'play-pressed'
  | 'cue-pressed'
  | 'cue-released'
  /** The playhead reached the end of the track. Not a user action, and it still has to be handled:
   * a deck that stays in `playing` at the last frame reports itself as playing forever. */
  | 'ended';

export interface TransportState {
  readonly phase: TransportPhase;
  /**
   * Where cue-released returns to, in frames.
   *
   * Zero until the first cue is set, which makes the track start the cue point by default — the
   * same as a freshly loaded CDJ, and the behaviour that means pressing cue on an untouched track
   * does something sensible rather than nothing.
   */
  readonly cuePointFrames: number;
}

export interface TransportTransition {
  readonly next: TransportState;
  /**
   * Where the playhead must move, or null to leave it alone.
   *
   * Returned rather than applied, because the thing that owns the playhead is the audio thread's
   * per-sample loop and this function is not allowed to know that. It also makes every transition
   * assertable in a unit test without a processor: the *decision* is here, the effect is the
   * caller's.
   */
  readonly seekToFrames: number | null;
}

export function initialTransport(): TransportState {
  return { phase: 'paused', cuePointFrames: 0 };
}

/** True while the playhead should be advancing. The one thing the per-sample loop asks. */
export function isRunning(phase: TransportPhase): boolean {
  return phase === 'playing' || phase === 'previewing';
}

/** True while audio should be audible. Identical to `isRunning` today — kept separate because
 * they are different questions, and FR-04's loop-roll will make them differ. (FR-06's keylock was
 * the other reason until P3.11 deferred it to FF-3; one reason is still a reason.) */
export function isAudible(phase: TransportPhase): boolean {
  return isRunning(phase);
}

/**
 * Applies one event. Total over every (phase, event) pair — see the table at the top of this file.
 *
 * `positionFrames` is where the playhead is *now*, needed only by the one transition that reads it
 * (`paused` + cue-pressed, which sets the cue there).
 */
/** `state` with a new phase, playhead untouched. */
function stay(state: TransportState, phase: TransportPhase): TransportTransition {
  return { next: { ...state, phase }, seekToFrames: null };
}

/** `state` with a new phase, playhead sent back to the cue. */
function toCue(state: TransportState, phase: TransportPhase): TransportTransition {
  return { next: { ...state, phase }, seekToFrames: state.cuePointFrames };
}

function fromPaused(
  state: TransportState,
  event: TransportEvent,
  positionFrames: number,
): TransportTransition {
  switch (event) {
    case 'play-pressed':
      return stay(state, 'playing');
    case 'cue-pressed':
      // "Set at pause point." The cue moves to wherever the deck is standing, and the preview
      // starts from there — so the playhead does not need to move at all.
      return {
        next: { phase: 'previewing', cuePointFrames: positionFrames },
        seekToFrames: null,
      };
    case 'cue-released':
    case 'ended':
      return stay(state, 'paused');
  }
}

function fromPlaying(state: TransportState, event: TransportEvent): TransportTransition {
  switch (event) {
    case 'play-pressed':
      // A CDJ's pause leaves the playhead where it is. It does not return to cue — that is what the
      // cue button is for, and conflating them loses the ability to pause mid-phrase.
      return stay(state, 'paused');
    case 'cue-pressed':
      return toCue(state, 'previewing');
    case 'cue-released':
      // Stale release: cue was pressed, play was pressed to latch in, now the finger lifts.
      return stay(state, 'playing');
    case 'ended':
      return stay(state, 'paused');
  }
}

function fromPreviewing(
  state: TransportState,
  event: TransportEvent,
): TransportTransition {
  switch (event) {
    case 'play-pressed':
      // Latch. Pressing play during a preview commits to playing, and the eventual cue release must
      // then do nothing — which `fromPlaying`'s cue-released case guarantees.
      return stay(state, 'playing');
    case 'cue-pressed':
      // Re-trigger from the cue without releasing. Cue-stutter is a technique, not an edge case.
      return toCue(state, 'previewing');
    case 'cue-released':
      return toCue(state, 'paused');
    case 'ended':
      // A preview that ran to the end still returns to the cue. Otherwise releasing would leave the
      // deck parked at the last frame, which is not where the finger said to go.
      return toCue(state, 'paused');
  }
}

export function transportEvent(
  state: TransportState,
  event: TransportEvent,
  positionFrames: number,
): TransportTransition {
  switch (state.phase) {
    case 'paused':
      return fromPaused(state, event, positionFrames);
    case 'playing':
      return fromPlaying(state, event);
    case 'previewing':
      return fromPreviewing(state, event);
  }
}

// No trailing `return` and no `break` between the cases above, and that is the point rather than an
// oversight: **both switches are exhaustive, and TypeScript proves it.** A default branch would
// compile, and it would also be the place a fourth phase or a fifth event silently landed as a
// no-op — which is this repo's most-repeated failure wearing a passing test suite. With the
// switches total, adding a union member makes this function stop compiling, at the moment the
// decision about what it should do still has to be made. `tests/unit/transport.test.ts` walks the
// full 3x4 product as the belt to this braces.

/**
 * The phase, as a small integer for the status SAB.
 *
 * A string cannot cross a SharedArrayBuffer without being encoded, and the audio thread must not
 * allocate to publish its own state. The UI decodes it back.
 */
export const TRANSPORT_PHASE_CODE: Record<TransportPhase, number> = {
  paused: 0,
  playing: 1,
  previewing: 2,
};

const PHASE_BY_CODE: readonly TransportPhase[] = ['paused', 'playing', 'previewing'];

export function phaseFromCode(code: number): TransportPhase {
  // An unknown code means the two sides of the buffer disagree — a stale worklet against a new UI.
  // `paused` is the safe reading: it claims less than the alternatives.
  return PHASE_BY_CODE[code] ?? 'paused';
}
